import React, { useMemo, useState } from "react";
import { useAtom } from "jotai";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Facebook,
  ImagePlus,
  Instagram,
  LayoutGrid,
  Loader2,
  MessageCircle,
  Package,
  Search,
  Sparkles,
  Store,
  Upload,
  X,
} from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "components/ui/dialog";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Textarea } from "components/ui/textarea";
import ChoiceCard from "components/choice-card";
import { cn } from "lib/utils";
import { BUDGET_PRESETS, DURATION_PRESETS, MIN_BOOST_BUDGET, boostDialogAtom, naira } from "lib/boost";
import { PLACEMENTS, createCampaignAction, placementLabel, uploadCreativeAction } from "action/ads-action";
import { getProductDataAction, myProductsKey, ProductList } from "action/product-action";
import { useUserData } from "store/user-data";

const STEPS = ["What to promote", "Ad creative", "Placements", "Budget"];
const DEFAULT_PLACEMENTS = ["FB_FEED", "IG_FEED", "FB_REELS_STORIES", "IG_REELS_STORIES"];
const ACCEPT = "image/jpeg,image/png,image/webp,video/mp4,video/quicktime";
const MAX_FILES = 10;

const PLACEMENT_ICONS: Record<string, React.ReactNode> = {
  FB_FEED: <Facebook className="h-4 w-4 text-default-500" />,
  IG_FEED: <Instagram className="h-4 w-4 text-default-500" />,
  FB_REELS_STORIES: <Facebook className="h-4 w-4 text-default-500" />,
  IG_REELS_STORIES: <Instagram className="h-4 w-4 text-default-500" />,
  MESSENGER: <MessageCircle className="h-4 w-4 text-default-500" />,
  AUDIENCE_NETWORK: <LayoutGrid className="h-4 w-4 text-default-500" />,
};

type Creative = { url: string; kind: "image" | "video"; name: string };

/**
 * The one "Boost on Meta Ads" flow, opened from the dashboard, the Meta Ads page and the product
 * screens. Four steps (what to promote, creative, placements, budget), then Paystack.
 */
export default function BoostOnMetaDialog() {
  const [state, setState] = useAtom(boostDialogAtom);
  const close = () => setState({ open: false });

  return (
    <Dialog open={state.open} onOpenChange={(open) => (open ? null : close())}>
      <DialogContent className="max-h-[92dvh] overflow-y-auto md:max-w-[640px]">
        {/* Mounted only while open, so every boost starts from a clean slate. */}
        {state.open && <BoostFlow initialProductId={state.productId} onDone={close} />}
      </DialogContent>
    </Dialog>
  );
}

function BoostFlow({ initialProductId, onDone }: { initialProductId?: string; onDone: () => void }) {
  const { userData } = useUserData();
  const [step, setStep] = useState(0);
  const [promote, setPromote] = useState<"STOREFRONT" | "PRODUCT">(initialProductId ? "PRODUCT" : "STOREFRONT");
  const [productId, setProductId] = useState<string | undefined>(initialProductId);
  const [productSearch, setProductSearch] = useState("");
  const [creativeType, setCreativeType] = useState<"UPLOAD" | "CREATE_FOR_ME">("CREATE_FOR_ME");
  const [creatives, setCreatives] = useState<Creative[]>([]);
  const [uploading, setUploading] = useState(0);
  const [notes, setNotes] = useState("");
  const [placements, setPlacements] = useState<string[]>(DEFAULT_PLACEMENTS);
  const [budget, setBudget] = useState<number>(10000);
  const [customBudget, setCustomBudget] = useState("");
  const [days, setDays] = useState(7);
  const [paying, setPaying] = useState(false);

  const { data: productsResponse, isLoading: productsLoading } = useQuery({
    queryKey: myProductsKey(userData?.business_id),
    queryFn: () => getProductDataAction(),
    enabled: !!userData?.business_id,
  });
  const products: ProductList[] = useMemo(
    () => (Array.isArray(productsResponse?.body) ? productsResponse!.body : []),
    [productsResponse],
  );
  const product = products.find((p) => p.id === productId);
  const shownProducts = useMemo(() => {
    const term = productSearch.trim().toLowerCase();
    return term ? products.filter((p) => p.name.toLowerCase().includes(term)) : products;
  }, [products, productSearch]);

  const budgetOk = Number.isFinite(budget) && budget >= MIN_BOOST_BUDGET;
  const stepProblem = [
    promote === "PRODUCT" && !product ? "Choose the product to promote." : null,
    uploading > 0
      ? "Wait for your files to finish uploading."
      : creativeType === "UPLOAD" && creatives.length === 0
        ? "Upload at least one image or video, or let us create the ad for you."
        : null,
    placements.length === 0 ? "Choose at least one place for your ad to show." : null,
    budgetOk ? null : `The minimum budget is ${naira(MIN_BOOST_BUDGET)}.`,
  ][step];

  const addFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    const room = MAX_FILES - creatives.length - uploading;
    const chosen = Array.from(files).slice(0, Math.max(0, room));
    if (chosen.length < files.length) toast.error(`You can add up to ${MAX_FILES} files.`);
    setUploading((n) => n + chosen.length);
    await Promise.all(
      chosen.map(async (file) => {
        const result = await uploadCreativeAction(file);
        setUploading((n) => n - 1);
        if (result.error || !result.body?.url) {
          toast.error(`${file.name}: ${result.error || "upload failed"}`);
          return;
        }
        setCreatives((list) => [...list, { url: result.body.url, kind: result.body.kind, name: file.name }]);
      }),
    );
  };

  const pay = async () => {
    setPaying(true);
    const result = await createCampaignAction({
      promote,
      productId: promote === "PRODUCT" ? productId : undefined,
      creativeType,
      creativeUrls: creativeType === "UPLOAD" ? creatives.map((c) => c.url) : [],
      creativeNotes: notes.trim(),
      placements,
      budget,
      durationDays: days,
    });
    if (result.error || !result.body?.authorization_url) {
      setPaying(false);
      toast.error(result.error || "Couldn't start the payment. Please try again.");
      return;
    }
    // Paystack brings the vendor back to /meta-ads once they've paid.
    window.location.assign(result.body.authorization_url);
  };

  const togglePlacement = (value: string) =>
    setPlacements((list) => (list.includes(value) ? list.filter((p) => p !== value) : [...list, value]));

  return (
    <div className="space-y-6">
      <DialogHeader className="space-y-3 pr-6">
        <DialogTitle className="font-display text-xl">Boost on Meta Ads</DialogTitle>
        <DialogDescription>
          Step {step + 1} of {STEPS.length} · {STEPS[step]}
        </DialogDescription>
        <div className="flex gap-1.5" aria-hidden>
          {STEPS.map((label, index) => (
            <span
              key={label}
              className={cn("h-1.5 flex-1 rounded-full", index <= step ? "bg-default-900" : "bg-default-200")}
            />
          ))}
        </div>
      </DialogHeader>

      {step === 0 && (
        <div className="space-y-4">
          <div role="radiogroup" aria-label="What to promote" className="grid gap-3 sm:grid-cols-2">
            <ChoiceCard
              selected={promote === "STOREFRONT"}
              onSelect={() => setPromote("STOREFRONT")}
              icon={<Store className="h-4 w-4 text-default-500" />}
              title="My storefront"
              hint="Send people to your whole store."
            />
            <ChoiceCard
              selected={promote === "PRODUCT"}
              onSelect={() => setPromote("PRODUCT")}
              icon={<Package className="h-4 w-4 text-default-500" />}
              title="A product"
              hint="Feature one product in the ad."
            />
          </div>

          {promote === "PRODUCT" && (
            <div className="space-y-3 rounded-[14px] bg-default-50 p-4">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-default-400" />
                <Input
                  value={productSearch}
                  onChange={(event) => setProductSearch(event.target.value)}
                  placeholder="Search your products"
                  className="pl-9"
                  aria-label="Search your products"
                />
              </div>
              <div role="radiogroup" aria-label="Product" className="max-h-64 space-y-1.5 overflow-y-auto pr-1">
                {productsLoading && <p className="py-4 text-center text-sm text-default-500">Loading products…</p>}
                {!productsLoading && products.length === 0 && (
                  <p className="py-4 text-center text-sm text-default-500">
                    You haven't added any products yet. Add one first, or boost your storefront.
                  </p>
                )}
                {shownProducts.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    role="radio"
                    aria-checked={item.id === productId}
                    onClick={() => setProductId(item.id)}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-xl border p-2 text-start transition-colors",
                      item.id === productId
                        ? "border-default-900 bg-card"
                        : "border-transparent hover:border-default-300 hover:bg-card",
                    )}
                  >
                    <span className="h-11 w-11 flex-none overflow-hidden rounded-lg bg-default-100">
                      {item.image_url && <img src={item.image_url} alt="" className="h-full w-full object-cover" />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-default-900">{item.name}</span>
                      <span className="block text-xs text-default-600">{naira(item.price, 2)}</span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {step === 1 && (
        <div className="space-y-4">
          <div role="radiogroup" aria-label="Ad creative" className="grid gap-3 sm:grid-cols-2">
            <ChoiceCard
              selected={creativeType === "UPLOAD"}
              onSelect={() => setCreativeType("UPLOAD")}
              icon={<Upload className="h-4 w-4 text-default-500" />}
              title="Upload my own"
              hint="Use your photos or videos."
            />
            <ChoiceCard
              selected={creativeType === "CREATE_FOR_ME"}
              onSelect={() => setCreativeType("CREATE_FOR_ME")}
              icon={<Sparkles className="h-4 w-4 text-default-500" />}
              title="Create it for me"
              hint="Our team designs the ad for you."
            />
          </div>

          {creativeType === "UPLOAD" && (
            <div className="space-y-3">
              <label
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => {
                  event.preventDefault();
                  addFiles(event.dataTransfer.files);
                }}
                className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-[14px] border border-dashed border-default-300 bg-default-50 px-4 py-8 text-center hover:border-default-500"
              >
                <ImagePlus className="h-6 w-6 text-default-500" />
                <span className="text-sm font-medium text-default-800">Drop images or videos here, or click to choose</span>
                <span className="text-xs text-default-500">JPG, PNG or WebP up to 10 MB · MP4 or MOV up to 50 MB</span>
                <input
                  type="file"
                  accept={ACCEPT}
                  multiple
                  className="sr-only"
                  onChange={(event) => {
                    addFiles(event.target.files);
                    event.target.value = "";
                  }}
                />
              </label>
              {(creatives.length > 0 || uploading > 0) && (
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
                  {creatives.map((creative) => (
                    <div key={creative.url} className="group relative aspect-square overflow-hidden rounded-xl bg-default-100">
                      {creative.kind === "video" ? (
                        <video src={creative.url} muted playsInline className="h-full w-full object-cover" />
                      ) : (
                        <img src={creative.url} alt={creative.name} className="h-full w-full object-cover" />
                      )}
                      <button
                        type="button"
                        onClick={() => setCreatives((list) => list.filter((c) => c.url !== creative.url))}
                        className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-white"
                        aria-label={`Remove ${creative.name}`}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                  {Array.from({ length: uploading }).map((_, index) => (
                    <div key={`uploading-${index}`} className="flex aspect-square items-center justify-center rounded-xl bg-default-100">
                      <Loader2 className="h-5 w-5 animate-spin text-default-500" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="space-y-2">
            <label htmlFor="boost-notes" className="text-sm font-medium text-default-800">
              {creativeType === "CREATE_FOR_ME" ? "Tell us about the ad (optional)" : "Anything else we should know? (optional)"}
            </label>
            <Textarea
              id="boost-notes"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              maxLength={2000}
              rows={3}
              placeholder="e.g. a discount to mention, the colours you like, who it's for"
            />
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-3">
          <p className="text-sm text-default-600">Where should your ad show? We suggest keeping the four main ones.</p>
          <div className="grid gap-3 sm:grid-cols-2">
            {PLACEMENTS.map((placement) => (
              <ChoiceCard
                key={placement.value}
                kind="checkbox"
                selected={placements.includes(placement.value)}
                onSelect={() => togglePlacement(placement.value)}
                icon={PLACEMENT_ICONS[placement.value]}
                title={placement.label}
              />
            ))}
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-5">
          <div className="space-y-2">
            <div className="text-sm font-medium text-default-800">Total budget</div>
            <div className="flex flex-wrap gap-2">
              {BUDGET_PRESETS.map((amount) => (
                <PresetButton
                  key={amount}
                  selected={!customBudget && budget === amount}
                  onClick={() => {
                    setCustomBudget("");
                    setBudget(amount);
                  }}
                >
                  {naira(amount)}
                </PresetButton>
              ))}
            </div>
            <div className="relative max-w-[220px]">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-default-500">₦</span>
              <Input
                inputMode="numeric"
                value={customBudget}
                onChange={(event) => {
                  const digits = event.target.value.replace(/[^\d]/g, "").slice(0, 9);
                  setCustomBudget(digits);
                  setBudget(digits ? Number(digits) : 10000);
                }}
                placeholder="Other amount"
                aria-label="Other budget amount"
                className="pl-7"
              />
            </div>
            <p className="text-xs text-default-500">Minimum {naira(MIN_BOOST_BUDGET)}.</p>
          </div>

          <div className="space-y-2">
            <div className="text-sm font-medium text-default-800">Run it for</div>
            <div className="flex flex-wrap gap-2">
              {DURATION_PRESETS.map((value) => (
                <PresetButton key={value} selected={days === value} onClick={() => setDays(value)}>
                  {value} days
                </PresetButton>
              ))}
            </div>
          </div>

          <dl className="space-y-2 rounded-[14px] bg-default-50 p-4 text-sm">
            <SummaryRow label="Promoting" value={promote === "PRODUCT" ? product?.name : "Your storefront"} />
            <SummaryRow
              label="Creative"
              value={creativeType === "UPLOAD" ? `${creatives.length} file${creatives.length === 1 ? "" : "s"} uploaded` : "Created by Storbolt"}
            />
            <SummaryRow label="Placements" value={placements.map(placementLabel).join(", ")} />
            <SummaryRow label="Budget" value={`${naira(budget)} over ${days} days (about ${naira(budget / days)} a day)`} />
          </dl>
          <p className="text-xs text-default-600">
            You'll pay securely with Paystack. After payment our team sets up your ad on Meta, and its results appear on your
            Meta Ads page.
          </p>
        </div>
      )}

      {stepProblem && <p className="text-xs text-default-500 sm:text-end">{stepProblem}</p>}
      <div className="flex flex-col-reverse gap-2 border-t border-default-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
        <Button type="button" variant="ghost" onClick={() => (step === 0 ? onDone() : setStep(step - 1))} disabled={paying}>
          {step === 0 ? "Cancel" : "Back"}
        </Button>
        {step < STEPS.length - 1 ? (
            <Button type="button" disabled={!!stepProblem} onClick={() => setStep(step + 1)}>
              Continue
            </Button>
          ) : (
            <Button type="button" disabled={!!stepProblem || paying} onClick={pay}>
              {paying && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Pay {naira(budget)} & Launch
            </Button>
          )}
      </div>
    </div>
  );
}

function PresetButton({ selected, onClick, children }: { selected: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        "h-9 rounded-full border px-4 text-sm font-medium transition-colors",
        selected
          ? "border-default-900 bg-default-900 text-default-foreground"
          : "border-default-300 bg-card text-default-800 hover:border-default-900",
      )}
    >
      {children}
    </button>
  );
}

function SummaryRow({ label, value }: { label: string; value?: React.ReactNode }) {
  return (
    <div className="flex gap-3">
      <dt className="w-24 flex-none text-default-500">{label}</dt>
      <dd className="min-w-0 flex-1 font-medium text-default-900">{value || "-"}</dd>
    </div>
  );
}
